import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { API_ENDPOINTS, API_HEADERS } from '../services/constants';
import '../styles/AracDegerlendirme.css';

interface AracBilgisi {
  plaka: string;
  uyeno: string;
  uyeid: string;
  sofor_adi: string;
  sofor_soyadi: string;
}

const AracDegerlendirme: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [aracBilgisi, setAracBilgisi] = useState<AracBilgisi | null>(null);
  const [puan, setPuan] = useState<number>(0);
  const [hoveredPuan, setHoveredPuan] = useState<number>(0);
  const [konu, setKonu] = useState<string>('');
  const [tip, setTip] = useState<string>('tesekkur');
  const [aciklama, setAciklama] = useState<string>('');
  const [iletisimTercihi, setIletisimTercihi] = useState<string>('yok');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    // URL parametrelerinden araç bilgilerini al
    const params = new URLSearchParams(location.search);
    const aracData = params.get('arac');
    
    if (aracData) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(aracData));
        setAracBilgisi(parsedData);
      } catch (error) {
        console.error('Araç bilgileri parse edilemedi:', error);
        navigate('/qr-kod-okuma');
      }
    } else {
      navigate('/qr-kod-okuma');
    }
  }, [location, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!aracBilgisi || puan === 0 || !konu.trim() || !aciklama.trim()) {
      alert('Lütfen tüm alanları doldurun ve puan verin.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.WEBSERVICE, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          method: 'sendAracDegerlendirme',
          uyeid: aracBilgisi.uyeid,
          adSoyad: 'Anonim Kullanıcı',
          plaka: aracBilgisi.plaka,
          sofor: `${aracBilgisi.sofor_adi} ${aracBilgisi.sofor_soyadi}`,
          puan: puan,
          konu: konu,
          tip: tip,
          aciklama: aciklama,
          iletisimTercihi: iletisimTercihi
        }),
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message);
        navigate('/qr-kod-okuma');
      } else {
        alert(result.message || 'Değerlendirme gönderilirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Değerlendirme gönderme hatası:', error);
      alert('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starIndex = index + 1;
      const isActive = starIndex <= (hoveredPuan || puan);
      
      return (
        <span
          key={starIndex}
          className={`star ${isActive ? 'active' : ''}`}
          onClick={() => setPuan(starIndex)}
          onMouseEnter={() => setHoveredPuan(starIndex)}
          onMouseLeave={() => setHoveredPuan(0)}
        >
          ★
        </span>
      );
    });
  };

  if (!aracBilgisi) {
    return (
      <div className="app-container">
        <Header title="Araç Değerlendirme" />
        <div className="page-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Araç bilgileri yükleniyor...</p>
          </div>
        </div>
        <TabBar />
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header title="Araç Değerlendirme" />
      <div className="page-container">
        <div className="degerlendirme-container">
          <div className="degerlendirme-header">
            <h2>Araç Değerlendirme</h2>
            <button 
              className="back-button"
              onClick={() => navigate('/qr-kod-okuma')}
            >
              ← Geri Dön
            </button>
          </div>

          <div className="arac-bilgi-card">
            <h3>Araç Bilgileri</h3>
            <div className="bilgi-item">
              <span className="label">Plaka:</span>
              <span className="value">{aracBilgisi.plaka}</span>
            </div>
            <div className="bilgi-item">
              <span className="label">Şoför:</span>
              <span className="value">{aracBilgisi.sofor_adi} {aracBilgisi.sofor_soyadi}</span>
            </div>
            <div className="bilgi-item">
              <span className="label">Üye No:</span>
              <span className="value">{aracBilgisi.uyeno}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="degerlendirme-form">
            <div className="form-group">
              <label>Değerlendirme Puanı *</label>
              <div className="star-rating">
                {renderStars()}
              </div>
              <small>Lütfen 1-5 arası puan verin</small>
            </div>

            <div className="form-group">
              <label htmlFor="tip">Değerlendirme Türü *</label>
              <select
                id="tip"
                value={tip}
                onChange={(e) => setTip(e.target.value)}
                required
              >
                <option value="tesekkur">Teşekkür</option>
                <option value="sikayet">Şikayet</option>
                <option value="oneri">Öneri</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="konu">Konu *</label>
              <input
                type="text"
                id="konu"
                value={konu}
                onChange={(e) => setKonu(e.target.value)}
                placeholder="Değerlendirme konusu"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="aciklama">Açıklama *</label>
              <textarea
                id="aciklama"
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                placeholder="Detaylı açıklama yazın..."
                rows={4}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="iletisim">İletişim Tercihi</label>
              <select
                id="iletisim"
                value={iletisimTercihi}
                onChange={(e) => setIletisimTercihi(e.target.value)}
              >
                <option value="yok">İletişim İstemiyorum</option>
                <option value="telefon">Telefon ile Aranabilir</option>
                <option value="email">E-posta Gönderebilir</option>
                <option value="sms">SMS Gönderebilir</option>
              </select>
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Gönderiliyor...' : 'Değerlendirmeyi Gönder'}
            </button>
          </form>
        </div>
      </div>
      <TabBar />
    </div>
  );
};

export default AracDegerlendirme; 